(() => {
  if (window.dryRunCampaignPricesInitialized) return;

  window.dryRunCampaignPricesInitialized = true;

  const priceSelector = '[data-dry-run-campaign-price][data-section-id]';
  const announcementSelector = '[data-dry-run-announcement][data-section-id]';
  let previousStickyAnnouncementTargets = new Set();
  let refreshTimer;
  let refreshInProgress = false;
  let refreshRequested = false;

  const getCampaignPrices = (root) => {
    const prices = Array.from(root.querySelectorAll(priceSelector));

    root.querySelectorAll('template').forEach((template) => {
      prices.push(...template.content.querySelectorAll(priceSelector));
    });

    return prices;
  };

  const syncCardSaleBadges = (scope = document) => {
    scope.querySelectorAll(priceSelector).forEach((campaignPrice) => {
      const productCard = campaignPrice.closest('.card-product');
      const saleBadge = productCard?.querySelector('[data-dry-run-card-sale-badge]');

      if (!saleBadge) return;

      const tagBadges = productCard.querySelectorAll('.badge--limited-edition, .badge--bestseller, .badge--new');
      const campaignSaleBadgeText = campaignPrice.dataset.campaignSaleBadgeText?.trim();
      const nativeSaleBadgeActive = saleBadge.dataset.nativeSaleBadgeActive === 'true';
      const badgeText = campaignSaleBadgeText || (nativeSaleBadgeActive ? saleBadge.dataset.nativeSaleBadgeText : '');

      saleBadge.textContent = badgeText;
      saleBadge.hidden = !badgeText;
      saleBadge.classList.toggle('hidden', !badgeText);

      tagBadges.forEach((tagBadge) => {
        tagBadge.hidden = Boolean(badgeText);
        tagBadge.classList.toggle('hidden', Boolean(badgeText));
      });
    });
  };

  const replaceCampaignPrices = (sectionId, sectionRefreshUrl, sectionHtml) => {
    const currentPrices = Array.from(document.querySelectorAll(priceSelector));
    const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newPrices = getCampaignPrices(parsedHtml);
    const sectionPrices = currentPrices.filter(
      (price) =>
        price.dataset.sectionId === sectionId &&
        (price.dataset.sectionRefreshUrl || '') === sectionRefreshUrl,
    );

    sectionPrices.forEach((currentPrice) => {
      const newPriceIndex = newPrices.findIndex(
        (price) =>
          price.dataset.sectionId === sectionId &&
          ((price.dataset.sectionRefreshUrl || '') === sectionRefreshUrl ||
            (sectionRefreshUrl && !price.dataset.sectionRefreshUrl)) &&
          price.dataset.productId === currentPrice.dataset.productId,
      );

      if (newPriceIndex === -1) return;

      const [newPrice] = newPrices.splice(newPriceIndex, 1);
      newPrice.dataset.sectionRefreshUrl = sectionRefreshUrl;
      currentPrice.replaceWith(newPrice);
      syncCardSaleBadges(newPrice.closest('.card-product') || document);
    });
  };

  const refreshSectionPrices = async (sectionId, sectionRefreshUrl) => {
    const url = new URL(sectionRefreshUrl || window.location.href, window.location.origin);

    if (!sectionRefreshUrl) {
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);
    }

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Unable to refresh campaign prices: ${response.status}`);

    replaceCampaignPrices(sectionId, sectionRefreshUrl, await response.text());
  };

  const refreshCampaignPrices = async () => {
    const sectionSources = new Map();

    document.querySelectorAll(priceSelector).forEach((price) => {
      const sectionId = price.dataset.sectionId;
      const sectionRefreshUrl = price.dataset.sectionRefreshUrl || '';

      if (sectionId) sectionSources.set(`${sectionId}:${sectionRefreshUrl}`, { sectionId, sectionRefreshUrl });
    });

    if (!sectionSources.size) return;

    await Promise.all(
      Array.from(sectionSources.values()).map(({ sectionId, sectionRefreshUrl }) =>
        refreshSectionPrices(sectionId, sectionRefreshUrl),
      ),
    );
  };

  const updateAnnouncementTimer = (announcement) => {
    const endTimestamp = Number(announcement.dataset.endTimestamp || 0);
    if (!Number.isFinite(endTimestamp) || endTimestamp <= 0) return;

    const remaining = Math.max(0, endTimestamp * 1000 - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);

    if (totalSeconds === 0) {
      if (!announcement.hidden) {
        announcement.hidden = true;
        updateAnnouncementHeight();
      }
      return;
    }

    const isLastDay = totalSeconds <= 86400;

    announcement.querySelectorAll('[data-dry-run-last-day-text]').forEach((element) => {
      element.hidden = !isLastDay;
    });

    announcement.querySelectorAll('[data-dry-run-supporting-text]').forEach((element) => {
      element.hidden = isLastDay;
    });

    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    Object.entries(values).forEach(([unit, value]) => {
      const element = announcement.querySelector(`[data-dry-run-${unit}]`);
      if (element) element.textContent = String(value).padStart(2, '0');
    });
  };

  const updateAnnouncementTimers = () => {
    document.querySelectorAll(announcementSelector).forEach(updateAnnouncementTimer);
  };

  const updateStickyAnnouncementStack = () => {
    const stickyItems = [];
    let announcementHeight = 0;

    document.querySelectorAll(announcementSelector).forEach((announcement) => {
      if (announcement.hidden) return;
      announcementHeight += announcement.offsetHeight;
      if (announcement.dataset.sticky !== 'true') return;
      const target = announcement.closest('.shopify-section');
      if (target) stickyItems.push({ target, type: 'dry-run' });
    });

    document.querySelectorAll('custom-announcement-bar').forEach((announcement) => {
      const stickyType = announcement.dataset.stickyType || 'none';
      const visiblePanels = Array.from(
        announcement.querySelectorAll('[data-campaign-panel], [data-announcement-panel]')
      ).filter((panel) => !panel.hidden);
      let target = null;

      announcementHeight += visiblePanels.reduce(
        (height, panel) => height + panel.offsetHeight,
        0
      );

      if (stickyType === 'announcement') {
        const announcementPanel = announcement.querySelector('[data-announcement-panel]');
        if (announcementPanel && !announcementPanel.hidden) target = announcementPanel;
      }

      if (target) stickyItems.push({ target, type: 'custom' });
    });

    const uniqueItems = Array.from(
      new Map(stickyItems.map((item) => [item.target, item])).values()
    ).sort((first, second) =>
      first.target.compareDocumentPosition(second.target) & 4 ? -1 : 1
    );
    const currentTargets = new Set(uniqueItems.map((item) => item.target));

    previousStickyAnnouncementTargets.forEach((target) => {
      if (!currentTargets.has(target)) target.style.removeProperty('top');
    });

    let totalHeight = 0;
    let dryRunHeight = 0;
    let customHeight = 0;

    uniqueItems.forEach((item) => {
      item.target.style.top = `${totalHeight}px`;
      const height = item.target.offsetHeight;
      totalHeight += height;
      if (item.type === 'dry-run') dryRunHeight += height;
      if (item.type === 'custom') customHeight += height;
    });

    previousStickyAnnouncementTargets = currentTargets;
    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${announcementHeight}px`,
    );
    document.documentElement.style.setProperty(
      '--dry-run-sticky-announcement-height',
      `${dryRunHeight}px`,
    );
    document.documentElement.style.setProperty(
      '--custom-sticky-announcement-height',
      `${customHeight}px`,
    );
    document.documentElement.style.setProperty(
      '--sticky-announcement-height',
      `${totalHeight}px`,
    );
  };

  window.updateStickyAnnouncementStack = updateStickyAnnouncementStack;

  const updateAnnouncementHeight = () => {
    const announcements = Array.from(document.querySelectorAll(announcementSelector));
    const visibleAnnouncement = announcements.find(
      (announcement) => !announcement.hidden,
    );
    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${visibleAnnouncement?.offsetHeight || 0}px`,
    );
    updateStickyAnnouncementStack();
  };

  const replaceAnnouncement = (sectionId, sectionHtml) => {
    const currentAnnouncement = document.querySelector(
      `${announcementSelector}[data-section-id="${CSS.escape(sectionId)}"]`,
    );
    if (!currentAnnouncement) return;

    const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newAnnouncement = parsedHtml.querySelector(announcementSelector);
    if (!newAnnouncement) return;

    currentAnnouncement.replaceWith(newAnnouncement);
    updateAnnouncementTimer(newAnnouncement);
    updateAnnouncementHeight();
  };

  const refreshAnnouncement = async (sectionId) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Unable to refresh campaign announcement: ${response.status}`);
    replaceAnnouncement(sectionId, await response.text());
  };

  const refreshAnnouncements = async () => {
    const sectionIds = new Set(
      Array.from(document.querySelectorAll(announcementSelector), (announcement) => announcement.dataset.sectionId).filter(
        Boolean,
      ),
    );

    await Promise.all(Array.from(sectionIds, refreshAnnouncement));
  };

  const runRefresh = async () => {
    if (refreshInProgress) {
      refreshRequested = true;
      return;
    }

    refreshInProgress = true;

    try {
      await Promise.all([refreshCampaignPrices(), refreshAnnouncements()]);
    } catch {
    } finally {
      refreshInProgress = false;

      if (refreshRequested) {
        refreshRequested = false;
        runRefresh();
      }
    }
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(runRefresh, 100);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
  }

  document.addEventListener('cartUpdated', scheduleRefresh);

  syncCardSaleBadges();
  updateAnnouncementTimers();
  updateAnnouncementHeight();
  window.setInterval(updateAnnouncementTimers, 1000);
  window.addEventListener('resize', updateAnnouncementHeight);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(priceSelector)) syncCardSaleBadges(node.parentElement || document);
        else if (node.querySelector(priceSelector)) syncCardSaleBadges(node);
        if (
          node.matches(announcementSelector)
          || node.querySelector(announcementSelector)
          || node.matches('custom-announcement-bar')
          || node.querySelector('custom-announcement-bar')
        ) {
          updateAnnouncementTimers();
          updateAnnouncementHeight();
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
